import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

import { cn } from "@bigpp/ui/lib/utils";

// AI Elements-inspired Sources: collapsible chip strip with per-chip hover
// preview. Kept compact so it lives inline under the assistant reply.

export type Source = { sourceId: string; url: string; title?: string };

const hostOf = (u: string): string => {
  try {
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return u;
  }
};

export function Sources({ sources }: { sources: readonly Source[] }) {
  const [open, setOpen] = useState(true);
  if (sources.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 pt-1">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="flex items-center gap-1 self-start text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
      >
        {open ? (
          <ChevronDown className="size-2.5" />
        ) : (
          <ChevronRight className="size-2.5" />
        )}
        Sources · {sources.length}
      </button>
      <div
        className={cn(
          "grid transition-all duration-200",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex flex-wrap gap-1 pt-0.5">
            {sources.map((s, i) => (
              <SourceChip key={s.sourceId} index={i + 1} source={s} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SourceChip({ index, source }: { index: number; source: Source }) {
  const host = hostOf(source.url);
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noreferrer"
      title={source.title ?? source.url}
      className="group/src flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-2 py-0.5 text-[10px] text-[var(--color-muted-foreground)] hover:bg-[var(--color-secondary)] hover:text-[var(--color-foreground)] hover:-translate-y-px transition-all"
    >
      <img
        src={`https://www.google.com/s2/favicons?domain=${host}&sz=16`}
        alt=""
        className="size-3 rounded-sm"
        aria-hidden
      />
      <span className="truncate max-w-[140px]">
        <span className="text-[var(--color-muted-foreground)]">{index}.</span>{" "}
        {source.title ?? host}
      </span>
    </a>
  );
}
